import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { PageHeader, StatCard, SectionCard, TempBadge, Empty } from "@/components/shared";
import { inr, mt, num } from "@/lib/format";
import { TrendingUp, Trophy, Flame, Target } from "lucide-react";

const STAGE_COLORS = { Registered: "#2563EB", Qualified: "#7C3AED", Quoted: "#0891B2", Negotiation: "#D97706", Won: "#059669", Lost: "#DC2626" };

export default function SalesCRM() {
  const nav = useNavigate();
  const [dash, setDash] = useState(null);
  const [cols, setCols] = useState([]);
  const [lost, setLost] = useState({ rows: [], competitor_mt: {} });
  const [routing, setRouting] = useState(null);

  useEffect(() => {
    api.get("/sales/dashboard").then((r) => setDash(r.data));
    api.get("/sales/pipeline").then((r) => setCols(r.data));
    api.get("/sales/lost").then((r) => setLost(r.data));
    api.get("/sales/routing").then((r) => setRouting(r.data));
  }, []);

  return (
    <div>
      <PageHeader title="Sales CRM" subtitle="Pipeline in ₹ and MT, lead temperature and stage transitions." testid="sales-crm" />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6 stagger">
        <StatCard label="Pipeline Value" value={dash ? inr(dash.pipeline_value) : "—"} sub={dash ? mt(dash.pipeline_mt) : ""} accent="#2563EB" icon={TrendingUp} testid="crm-pipeline" />
        <StatCard label="Won Deals" value={dash?.won ?? "—"} sub={dash ? inr(dash.won_value) : ""} accent="#059669" icon={Trophy} testid="crm-won" />
        <StatCard label="Win Rate" value={dash ? `${dash.win_rate}%` : "—"} sub="of all projects" accent="#D97706" icon={Target} testid="crm-winrate" />
        <StatCard label="Hot Leads" value={dash?.temperature?.Hot ?? "—"} sub={`${dash?.temperature?.Warm || 0} warm · ${dash?.temperature?.Cold || 0} cold`} accent="#DC2626" icon={Flame} testid="crm-hot" />
      </div>

      <SectionCard title="Pipeline Board" className="mb-6" testid="sales-crm-kanban-board">
        <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-6 gap-3 overflow-x-auto">
          {cols.map((c) => (
            <div key={c.stage} className="min-w-[160px]">
              <div className="flex items-center justify-between mb-2 px-1">
                <span className="text-xs font-bold uppercase tracking-wider" style={{ color: STAGE_COLORS[c.stage] }}>{c.stage}</span>
                <span className="text-xs font-mono text-slate-400">{c.count}</span>
              </div>
              <div className="text-[11px] text-slate-500 mb-2 px-1 font-mono">{inr(c.value)} · {mt(c.mt)}</div>
              <div className="space-y-2 max-h-[420px] overflow-auto pr-1">
                {c.items.slice(0, 20).map((p) => (
                  <div key={p.id} data-testid={`kanban-card-${p.project_id}`} onClick={() => nav(`/projects/${p.id}`)} className="bg-white border border-slate-200 rounded-lg p-2.5 cursor-pointer hover:border-[var(--copper)] hover:shadow-sm transition">
                    <p className="text-xs font-medium text-slate-800 line-clamp-2">{p.name}</p>
                    <div className="flex items-center justify-between mt-1.5">
                      <span className="font-mono text-[11px] text-slate-600">{mt(p.tonnage_mt)}</span>
                      <TempBadge temp={p.temperature} />
                    </div>
                    <p className="font-mono text-[11px] text-[var(--copper)] font-semibold mt-1">{inr(p.est_value_inr)}</p>
                  </div>
                ))}
                {c.items.length === 0 && <p className="text-xs text-slate-300 px-1">Empty</p>}
              </div>
            </div>
          ))}
        </div>
      </SectionCard>

      <div className="grid lg:grid-cols-2 gap-6">
        <SectionCard title="Lost Opportunities & Competitors" testid="lost-opportunities">
          {Object.keys(lost.competitor_mt).length > 0 && (
            <div className="flex flex-wrap gap-2 mb-4">
              {Object.entries(lost.competitor_mt).map(([c, v]) => (
                <span key={c} className="text-xs px-2.5 py-1 rounded-full bg-red-50 text-red-600 border border-red-100 font-medium">{c}: {v.toFixed(1)} MT</span>
              ))}
            </div>
          )}
          {lost.rows.length === 0 ? <Empty /> : (
            <div className="space-y-2 max-h-72 overflow-auto">
              {lost.rows.map((l) => (
                <div key={l.id} className="flex justify-between items-center py-2 border-b border-slate-50 text-sm">
                  <div><p className="text-slate-700">{l.project_name}</p><p className="text-xs text-slate-400">{l.reason} · to {l.competitor || "—"}</p></div>
                  <span className="font-mono text-red-600 text-xs">{mt(l.lost_mt)}</span>
                </div>
              ))}
            </div>
          )}
        </SectionCard>

        <SectionCard title="Lead Routing & SLA" testid="routing-sla">
          {routing && (
            <>
              <div className="flex items-center justify-between mb-4">
                <div><p className="text-xs uppercase text-slate-400 font-semibold">SLA Compliance</p><p className="text-2xl font-extrabold font-mono">{routing.sla.compliance}%</p></div>
                <p className="text-xs text-slate-500">{routing.sla.assigned}/{routing.sla.total} routed</p>
              </div>
              <div className="space-y-2">
                {routing.rules.map((r) => (
                  <div key={r.id} className="flex justify-between items-center py-2 border-b border-slate-50 text-sm">
                    <div><p className="text-slate-700">{r.name}</p><p className="text-xs text-slate-400">{r.city} · {r.segment}</p></div>
                    <span className="text-xs text-slate-500">{r.assignee} · {r.sla_hours}h</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </SectionCard>
      </div>
    </div>
  );
}
